import React, { useEffect, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import { t } from 'i18next'
import { useDispatch } from 'react-redux'
import FilterSelector from 'Components/FilterSelector'
import MiniLoading from 'Components/MiniLoading'
import { fetchCompanies } from 'redux/actions/companyAction'
import { fetchListWharehouse } from 'redux/actions/clientsActions'

const normalizeId = id => id?.toLowerCase?.() || id

const getWarehouseCode = warehouse =>
  warehouse.WarehouseCode || warehouse.Code || warehouse.BranchCode || ''

function WarehousePickerModal({
  open,
  selectedWarehouses,
  onClose,
  onConfirm,
}) {
  const dispatch = useDispatch()

  const [companies, setCompanies] = useState([])
  const [selectedCompany, setSelectedCompany] = useState('')
  const [searchText, setSearchText] = useState('')
  const [warehouses, setWarehouses] = useState([])
  const [loading, setLoading] = useState(false)
  const [checkedIds, setCheckedIds] = useState([])
  const [checkedWarehousesById, setCheckedWarehousesById] = useState({})
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!open) return

    setCheckedIds(
      selectedWarehouses.map(warehouse => normalizeId(warehouse.WarehouseId))
    )
    setCheckedWarehousesById(
      selectedWarehouses.reduce((acc, warehouse) => {
        acc[normalizeId(warehouse.WarehouseId)] = warehouse
        return acc
      }, {})
    )
    setSearchText('')
    setWarehouses([])

    const loadCompanies = async () => {
      try {
        const data = await dispatch(fetchCompanies())
        setCompanies(data)
        setSelectedCompany(data[0]?.CompanyId || '')
      } catch (error) {
        setCompanies([])
        setSelectedCompany('')
      }
    }

    loadCompanies().then()
  }, [open])

  useEffect(() => {
    if (!open || !selectedCompany) return

    const loadWarehouses = async () => {
      const requestId = ++requestIdRef.current
      setLoading(true)
      try {
        const payload = {
          companyId: selectedCompany,
          tenantId: 2,
          SearchText: null,
          PageNumber: 1,
          PageSize: 100,
        }
        const branches = await dispatch(fetchListWharehouse(payload))

        if (requestId !== requestIdRef.current) return

        const companyName =
          companies.find(company => company.CompanyId === selectedCompany)
            ?.Name || ''
        const mapped = (branches || [])
          .flatMap(branch => branch.Warehouses || [])
          .map(warehouse => ({
            ...warehouse,
            WarehouseId: normalizeId(warehouse.WarehouseId),
            WarehouseCode: getWarehouseCode(warehouse),
            CompanyName: warehouse.CompanyName || companyName,
          }))
          .filter(warehouse => warehouse.Description !== undefined)

        setWarehouses(mapped)
      } catch (error) {
        if (requestId !== requestIdRef.current) return
        setWarehouses([])
      } finally {
        if (requestId === requestIdRef.current) setLoading(false)
      }
    }

    loadWarehouses().then()
  }, [open, selectedCompany, companies, dispatch])

  const toggleWarehouse = (warehouse, checked) => {
    const warehouseId = normalizeId(warehouse.WarehouseId)

    setCheckedIds(prev =>
      checked
        ? Array.from(new Set([...prev, warehouseId]))
        : prev.filter(id => id !== warehouseId)
    )

    if (checked) {
      setCheckedWarehousesById(prev => ({
        ...prev,
        [warehouseId]: warehouse,
      }))
    }
  }

  const visibleWarehouses = searchText
    ? warehouses.filter(warehouse => {
        const normalizedSearch = searchText.toLowerCase()

        return (
          warehouse.Description?.toLowerCase().includes(normalizedSearch) ||
          warehouse.WarehouseCode?.toLowerCase().includes(normalizedSearch) ||
          warehouse.CompanyName?.toLowerCase().includes(normalizedSearch)
        )
      })
    : warehouses

  const handleSelectAll = () => {
    visibleWarehouses.forEach(warehouse => toggleWarehouse(warehouse, true))
  }

  const handleClearAll = () => {
    setCheckedIds(prev =>
      prev.filter(
        id => !visibleWarehouses.some(warehouse => warehouse.WarehouseId === id)
      )
    )
  }

  const handleSearchSubmit = event => {
    event.preventDefault()
  }

  const handleConfirm = () => {
    const confirmedWarehouses = checkedIds.map(id => {
      const existing = checkedWarehousesById[id]
      return {
        WarehouseId: id,
        Description: existing?.Description,
      }
    })
    onConfirm(confirmedWarehouses)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="modal-warehouse-picker">
      <DialogTitle>
        {t('QUICK_LIST_SETTINGS.PICKER.WAREHOUSES_TITLE')}
      </DialogTitle>
      <DialogContent>
        <div className="quick-list-picker-filters">
          <FilterSelector
            label={companies.find(c => c.CompanyId === selectedCompany)?.Name}
            options={companies}
            getOptionValue={item => item.CompanyId}
            getOptionLabel={item => item.Name}
            value={selectedCompany}
            onChange={item => setSelectedCompany(item.CompanyId)}
            placeholder={t('QUICK_LIST_SETTINGS.PICKER.COMPANY')}
          />
          <form
            onSubmit={handleSearchSubmit}
            className="quick-list-picker-search">
            <input
              type="text"
              className="form-control"
              placeholder={t(
                'QUICK_LIST_SETTINGS.PICKER.SEARCH_WAREHOUSE_PLACEHOLDER'
              )}
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              data-testid="input-warehouse-search"
            />
            <button type="submit" className="btn-add">
              {t('QUICK_LIST_SETTINGS.PICKER.SEARCH_BUTTON')}
            </button>
          </form>
        </div>

        <div className="quick-list-picker-bulk-actions">
          <button
            type="button"
            disabled={visibleWarehouses.length === 0}
            className="quick-list-picker-link"
            onClick={handleSelectAll}>
            {t('QUICK_LIST_SETTINGS.PICKER.SELECT_ALL')}
          </button>
          <button
            type="button"
            disabled={visibleWarehouses.length === 0}
            className="quick-list-picker-link clear"
            onClick={handleClearAll}>
            {t('QUICK_LIST_SETTINGS.PICKER.CLEAR_ALL')}
          </button>
        </div>

        <div className="quick-list-picker-list">
          {loading ? (
            <MiniLoading noMargin />
          ) : visibleWarehouses.length === 0 ? (
            <p>{t('QUICK_LIST_SETTINGS.PICKER.NO_RESULTS')}</p>
          ) : (
            visibleWarehouses.map((warehouse, index) => {
              const warehouseId = normalizeId(warehouse.WarehouseId)
              const isChecked = checkedIds.includes(warehouseId)
              return (
                <label
                  key={warehouseId || index}
                  className={`quick-list-picker-item${
                    isChecked ? ' selected' : ''
                  }`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={event =>
                      toggleWarehouse(warehouse, event.target.checked)
                    }
                    data-testid={`checkbox-warehouse-${warehouseId}`}
                  />
                  <span className="quick-list-picker-item-name">
                    {warehouse.Description}
                  </span>
                  <span className="quick-list-picker-item-sku">
                    {warehouse.WarehouseCode}
                  </span>
                  <span className="quick-list-picker-item-sku">
                    {warehouse.CompanyName}
                  </span>
                </label>
              )
            })
          )}
        </div>

        <div className="quick-list-picker-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            data-testid="btn-cancel-warehouse-picker">
            {t('QUICK_LIST_SETTINGS.PICKER.CANCEL')}
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleConfirm}
            data-testid="btn-confirm-warehouse-picker">
            {t('QUICK_LIST_SETTINGS.PICKER.CONFIRM')} ({checkedIds.length})
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default WarehousePickerModal
